import React from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import request from 'request';
import { useDispatch, useSelector } from 'store/hooks';
import Navbar from 'components/navbar';
import Loading from 'components/loading';
import styles from './application.module.scss';

const ApplicationLayout = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const application = useSelector((state) => state.application);
	const [ocrPending, setOcrPending] = React.useState(false);
	const [applicationPending, setApplicationPending] = React.useState(false);
	const [hasLegalCase, setHasLegalCase] = React.useState(false);
	const [legalCaseHasCorrectType, setLegalCaseHasCorrectType] = React.useState(null);
	const [guarantorsHasLegalCase, setGuarantorsHasLegalCase] = React.useState(
		new Array(application.data?.number_of_guarantors).fill(false),
	);
	const [guarantorsLegalCaseCorrectType, setGuarantorsLegalCaseCorrectType] = React.useState<
		('Yes' | 'No' | null)[]
	>(new Array(application.data?.number_of_guarantors).fill(null));

	const handleRunOCR = async (fileCRIFOCR: File | null, fileCRIFGuarantors: (File | null)[]) => {
		if (!fileCRIFOCR) return toast.error('Please upload CRIF file');
		if (fileCRIFGuarantors.find((fg) => fg === null))
			return toast.error('Please upload CRIF Guarantors file');
		setOcrPending(true);
		const fd = new FormData();
		fd.append('application_id', id!);
		fd.append('crif', fileCRIFOCR);

		for (let fileCRIFGuarantor of fileCRIFGuarantors)
			if (fileCRIFGuarantor) fd.append('crif_gurantor', fileCRIFGuarantor);

		request({
			url: '/applications/ocr',
			method: 'POST',
			data: fd,
			headers: { 'Content-Type': 'multipart/form-data' },
		})
			.then((res) => res.data)
			.then(() => dispatch.application.FETCH_APPLICATION_BY_ID_NO_PENDING(id))
			.catch((res: AxiosError) => {
				if (res.response?.status === 422) toast.error('Please upload CRIF file');
			})
			.finally(() => setOcrPending(false));
	};

	const handleRunLegalCase = async () => {
		await request
			.post(`/applications/${id}/legal_case`, {
				legal_case_has_correct_type: !hasLegalCase ? null : legalCaseHasCorrectType,
			})
			.catch((e) => toast.error(JSON.stringify(e.response?.data?.detail?.[0])));

		let guarantorLegalCases: { [key: string]: 'Yes' | 'No' | null } = {};

		for (let i = 0; i < guarantorsHasLegalCase.length; i++)
			guarantorLegalCases['guarantor' + (i + 1)] = guarantorsLegalCaseCorrectType[i];

		await request
			.post(`/applications/${id}/guarantor_legal_case`, {
				legal_case_has_correct_type: guarantorLegalCases,
			})
			.catch((e) => toast.error(JSON.stringify(e.response?.data?.detail?.[0])));
	};

	React.useLayoutEffect(() => {
		dispatch.application.REMOVE();
		dispatch.application.FETCH_APPLICATION_BY_ID({ id, onEnd: () => setApplicationPending(false) });
		dispatch.application.FETCH_APPLICATION_BY_ID({ id, onEnd: () => setApplicationPending(false) });
	}, [dispatch.application, id]);

	React.useEffect(() => {
		setGuarantorsHasLegalCase(new Array(application.data?.number_of_guarantors).fill(false));
		setGuarantorsLegalCaseCorrectType(new Array(application.data?.number_of_guarantors).fill(null));
	}, [application.data?.number_of_guarantors]);

	return (
		<div className={styles.applicationLayout} id="application-layout">
			{applicationPending || !application.data ? (
				<div className={styles.loading}>
					<Loading />
				</div>
			) : (
				<div className={styles.container}>
					<div className={styles.navbar}>
						<Navbar
							ocrPending={ocrPending}
							resultBlocked={
								application.data?.expert_decision === null && application.data?.is_approved === null
							}
						/>
					</div>
					<div className={styles.content}>
						<Outlet
							context={
								{
									hasLegalCase,
									setHasLegalCase,
									legalCaseHasCorrectType,
									setLegalCaseHasCorrectType,
									ocrPending,
									handleRunOCR,
									handleRunLegalCase,
									guarantorsHasLegalCase,
									setGuarantorsHasLegalCase,
									guarantorsLegalCaseCorrectType,
									setGuarantorsLegalCaseCorrectType,
								} as ContextType
							}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

type ContextType = {
	hasLegalCase: boolean;
	setHasLegalCase: React.Dispatch<React.SetStateAction<boolean>>;
	legalCaseHasCorrectType: 'Yes' | 'No' | null;
	setLegalCaseHasCorrectType: React.Dispatch<React.SetStateAction<'Yes' | 'No' | null>>;
	ocrPending: boolean;
	handleRunOCR: (fileCRIFOCR: File | null, fileCRIFGuarantors: (File | null)[]) => void;
	handleRunLegalCase: () => Promise<void>;
	guarantorsHasLegalCase: boolean[];
	setGuarantorsHasLegalCase: React.Dispatch<React.SetStateAction<boolean[]>>;
	guarantorsLegalCaseCorrectType: ('Yes' | 'No' | null)[];
	setGuarantorsLegalCaseCorrectType: React.Dispatch<React.SetStateAction<('Yes' | 'No' | null)[]>>;
};

export const useOCR = () => {
	return useOutletContext<ContextType>();
};

export default ApplicationLayout;
